/**
 * The Matrix: a node graph in the shape of IdleVoid's Void Matrix (GDD §5.6),
 * scaled for a phone.
 *
 * A five-spoke wheel: each spoke runs from the Origin to a Crown at the rim,
 * three ring roads cross between neighbouring spokes, and every spoke meets
 * every ring at a Nexus notable. Links are undirected — a node can be
 * allocated when any neighbour is, and refunded when the rest still reaches
 * the Origin. Nodes cost TE.
 */

import type { FleetStat } from './resources';

export type NodeKind = 'core' | 'minor' | 'notable' | 'crown';

export interface StatAmount {
  readonly stat: FleetStat;
  readonly amount: number;
}

export interface MatrixNode {
  readonly id: string;
  readonly kind: NodeKind;
  readonly spoke: string | null;
  readonly label: string;
  /** VS for the first level; each further level costs ×LEVEL_COST_GROWTH more. */
  readonly cost: number;
  /** Levels, as IPM's Space Station nodes have (Mining 1, Mining 2, …). Crowns are one-off. */
  readonly maxLevel: number;
  readonly x: number;
  readonly y: number;
  /** Effects of ONE level; a node at level L gives L × these (crowns: just these). */
  readonly effects: readonly StatAmount[];
  readonly neighbours: readonly string[];
}

/** Levels per node kind, and the share of the old single-allocation value one level gives. */
const LEVELS: Readonly<Record<NodeKind, { max: number; share: number }>> = {
  core: { max: 0, share: 0 },
  minor: { max: 5, share: 0.6 },
  notable: { max: 3, share: 0.7 },
  crown: { max: 1, share: 1 },
};
export const LEVEL_COST_GROWTH = 1.5;

/** VS to raise a node from `level` to `level + 1`. Null at max. */
export function levelCost(node: MatrixNode, level: number): number | null {
  if (level >= node.maxLevel) return null;
  return Math.ceil(node.cost * Math.pow(LEVEL_COST_GROWTH, level));
}

interface RouteDef {
  readonly label: string;
  readonly primary: FleetStat;
  readonly secondary: FleetStat;
}

export interface SpokeDef {
  readonly id: string;
  readonly label: string;
  readonly color: string;
  readonly stem: readonly [FleetStat, FleetStat];
  readonly routes: readonly [RouteDef, RouteDef];
  readonly crown: { readonly label: string; readonly description: string; readonly effects: readonly StatAmount[] };
}

const fx = (stat: FleetStat, amount: number): StatAmount => ({ stat, amount });

export const SPOKES: readonly SpokeDef[] = [
  {
    id: 'extraction', label: 'Extraction', color: '#ff3d7f', stem: ['mine', 'haste'],
    routes: [{ label: 'Deep Core', primary: 'mine', secondary: 'cargo' }, { label: 'Overclock', primary: 'haste', secondary: 'mine' }],
    crown: { label: 'Strip Mining', description: 'Planets give up far more ore; the market notices the glut.', effects: [fx('mine', 0.3), fx('sell', -0.1)] },
  },
  {
    id: 'logistics', label: 'Logistics', color: '#4dd9f5', stem: ['ship', 'cargo'],
    routes: [{ label: 'Express Lanes', primary: 'ship', secondary: 'haste' }, { label: 'Bulk Freight', primary: 'cargo', secondary: 'ship' }],
    crown: { label: 'Convoy Doctrine', description: 'Ships carry vastly more and fly a little slower.', effects: [fx('cargo', 0.4), fx('ship', -0.1)] },
  },
  {
    id: 'refinery', label: 'Refinery', color: '#9b7bff', stem: ['craft', 'smelt'],
    routes: [{ label: 'Crucible', primary: 'smelt', secondary: 'smeltYield' }, { label: 'Assembly', primary: 'craft', secondary: 'craftYield' }],
    crown: { label: 'Ceaseless Forge', description: 'Every workshop runs hotter; the drills are starved of crews.', effects: [fx('smelt', 0.25), fx('craft', 0.25), fx('mine', -0.1)] },
  },
  {
    id: 'commerce', label: 'Commerce', color: '#f5c542', stem: ['sell', 'discount'],
    routes: [{ label: 'Brokerage', primary: 'sell', secondary: 'te' }, { label: 'Charter', primary: 'discount', secondary: 'sell' }],
    crown: { label: 'Fold Futures', description: 'Sell the next galaxy short: more TE, thinner margins now.', effects: [fx('te', 0.25), fx('sell', -0.1)] },
  },
  {
    id: 'alchemy', label: 'Alchemy', color: '#4fb8ff', stem: ['smeltYield', 'craftYield'],
    routes: [{ label: 'Transmutation', primary: 'smeltYield', secondary: 'smelt' }, { label: 'Replication', primary: 'craftYield', secondary: 'craft' }],
    crown: { label: "Philosopher's Engine", description: 'Something from nothing, at the cost of time.', effects: [fx('smeltYield', 0.2), fx('craftYield', 0.2), fx('haste', -0.05)] },
  },
];

// ── Geometry ──────────────────────────────────────────────────────────────

export const RING_RADII = [200, 400, 600] as const;
export const CROWN_RADIUS = 780;
/** Minor nodes between the origin, each ring, and the crown. */
const SPOKE_SEGMENTS: readonly (readonly number[])[] = [[65, 135], [265, 335], [465, 535], [690]];
/** Nodes on each ring road between two neighbouring sockets. */
const ARC_COUNTS = [2, 3, 5] as const;

export function spokeAngle(index: number): number {
  return ((-90 + (360 / SPOKES.length) * index) * Math.PI) / 180;
}

const polar = (radius: number, angle: number) => ({
  x: Math.round(Math.cos(angle) * radius),
  y: Math.round(Math.sin(angle) * radius),
});

/** Cost grows with distance: 1–2 TE near the origin, ~15 on the rim. */
function costAt(radius: number, kind: NodeKind): number {
  const t = Math.max(0, Math.min(1, radius / CROWN_RADIUS));
  const base = 1 + 14 * t ** 1.5;
  if (kind === 'crown') return 25;
  if (kind === 'notable') return Math.round(base * 1.5 + 1);
  return Math.max(1, Math.round(base));
}

/** Yields and discount have hard caps, so their nodes pay half. */
const HALF: ReadonlySet<FleetStat> = new Set<FleetStat>(['smeltYield', 'craftYield', 'discount']);

function amountFor(stat: FleetStat, cost: number, kind: NodeKind): number {
  const value = kind === 'notable' ? 0.04 + cost * 0.01 : 0.01 + cost * 0.004;
  return Number((HALF.has(stat) ? value / 2 : value).toFixed(4));
}

// ── Build ─────────────────────────────────────────────────────────────────

interface Draft {
  id: string;
  kind: NodeKind;
  spoke: string | null;
  label: string;
  radius: number;
  x: number;
  y: number;
  stat?: FleetStat;
  fixed?: readonly StatAmount[];
  neighbours: Set<string>;
}

const STAT_SHORT: Readonly<Record<FleetStat, string>> = {
  mine: 'Mining', ship: 'Thrust', cargo: 'Holds', smelt: 'Smelting', craft: 'Crafting', haste: 'Tempo',
  smeltYield: 'Smelt Yield', craftYield: 'Craft Yield', discount: 'Charter', sell: 'Trade', te: 'Tithe',
  alloyValue: 'Bar Value', itemValue: 'Item Value',
};

function build(): readonly MatrixNode[] {
  const drafts = new Map<string, Draft>();
  const add = (d: Omit<Draft, 'neighbours'>): void => { drafts.set(d.id, { ...d, neighbours: new Set() }); };
  const link = (a: string, b: string): void => {
    drafts.get(a)!.neighbours.add(b);
    drafts.get(b)!.neighbours.add(a);
  };
  const nexusId = (arm: number, ring: number) => `mx:${SPOKES[arm]!.id}:nexus:${ring + 1}`;

  add({ id: ORIGIN, kind: 'core', spoke: null, label: 'Void Origin', radius: 0, x: 0, y: 0 });

  SPOKES.forEach((spoke, arm) => {
    const angle = spokeAngle(arm);
    let previous = ORIGIN;
    SPOKE_SEGMENTS.forEach((segment, band) => {
      segment.forEach((radius, step) => {
        const id = `mx:${spoke.id}:spoke:${band + 1}.${step + 1}`;
        const stat = spoke.stem[(band + step) % 2]!;
        add({ id, kind: 'minor', spoke: spoke.id, label: `${spoke.label} ${STAT_SHORT[stat]}`, radius, ...polar(radius, angle), stat });
        link(previous, id);
        previous = id;
      });
      if (band < RING_RADII.length) {
        const radius = RING_RADII[band]!;
        const id = nexusId(arm, band);
        const stat = spoke.stem[band % 2]!;
        add({ id, kind: 'notable', spoke: spoke.id, label: `${spoke.label} Nexus ${band + 1}`, radius, ...polar(radius, angle), stat });
        link(previous, id);
        previous = id;
      }
    });
    const crown = `mx:${spoke.id}:crown`;
    add({ id: crown, kind: 'crown', spoke: spoke.id, label: spoke.crown.label, radius: CROWN_RADIUS, ...polar(CROWN_RADIUS, angle), fixed: spoke.crown.effects });
    link(previous, crown);
  });

  RING_RADII.forEach((radius, ring) => {
    const count = ARC_COUNTS[ring]!;
    SPOKES.forEach((from, arm) => {
      const nextArm = (arm + 1) % SPOKES.length;
      const to = SPOKES[nextArm]!;
      let previous = nexusId(arm, ring);
      for (let index = 0; index < count; index += 1) {
        const t = (index + 1) / (count + 1);
        const angle = spokeAngle(arm) + t * ((2 * Math.PI) / SPOKES.length);
        const notable = ring > 0 && index === (count - 1) / 2;
        const route = ring > 0 ? from.routes[ring - 1]! : null;
        const stat = notable ? route!.primary : index % 2 === 0 ? (route?.secondary ?? from.stem[1]) : to.stem[ring % 2]!;
        const id = `mx:ring${ring + 1}:${from.id}-${to.id}:${index + 1}`;
        add({
          id, kind: notable ? 'notable' : 'minor', spoke: t <= 0.5 ? from.id : to.id,
          label: notable ? `${route!.label}` : `Road ${STAT_SHORT[stat]}`,
          radius, ...polar(radius, angle), stat,
        });
        link(previous, id);
        previous = id;
      }
      link(previous, nexusId(nextArm, ring));
    });
  });

  return [...drafts.values()].map((d): MatrixNode => {
    const cost = d.kind === 'core' ? 0 : costAt(d.radius, d.kind);
    const { max, share } = LEVELS[d.kind];
    const effects = d.fixed ?? (d.stat ? [fx(d.stat, Number((amountFor(d.stat, cost, d.kind) * share).toFixed(4)))] : []);
    return { id: d.id, kind: d.kind, spoke: d.spoke, label: d.label, cost, maxLevel: max, x: d.x, y: d.y, effects, neighbours: [...d.neighbours] };
  });
}

export const ORIGIN = 'mx:origin';
export const MATRIX_NODES: readonly MatrixNode[] = build();
export const MATRIX_BY_ID: ReadonlyMap<string, MatrixNode> = new Map(MATRIX_NODES.map((n) => [n.id, n]));

export function matrixNode(id: string): MatrixNode {
  const node = MATRIX_BY_ID.get(id);
  if (!node) throw new Error(`Unknown matrix node: ${id}`);
  return node;
}

export function spokeColor(spoke: string | null): string {
  return SPOKES.find((s) => s.id === spoke)?.color ?? '#e8eefc';
}
